<template>
	<div :id="uid" ref="rootRef">
		<slot />
	</div>
</template>

<script setup lang="ts">
import { provide, inject, reactive, computed, onMounted, onUnmounted, ref, nextTick, useTemplateRef } from 'vue';
import type { ComputedRef } from 'vue';

import { efFormSymbol } from './efFormSymbol';
import useUid from '@lib/composables/useUid';

const FOCUSABLE = 'input:not([type="hidden"]), select, textarea, button, [tabindex]:not([tabindex="-1"])';

interface Props {
	/**
	 * Lets the form be saved with required fields still empty, for someone who fills in another
	 * person's record bit by bit (HR). The fields keep their star and their hint; a value that is
	 * there and wrong still holds the form back. A nested form inherits it.
	 */
	allowEmpty?: boolean;
	/**
	 * Leaves the star off the labels, for a form where every field is required (sign in, set a
	 * password) and the star tells nobody anything. The fields stay required and keep their
	 * hint; only the mark goes, so keep it on wherever a field is optional. A nested form
	 * inherits it.
	 */
	hideRequiredMarks?: boolean;
}

const props = withDefaults(defineProps<Props>(), { allowEmpty: false, hideRequiredMarks: false });

const uid = useUid();
const parent = inject(efFormSymbol, undefined);
const rootRef = useTemplateRef<HTMLElement>('rootRef');

/** Set by reveal(): the empty fields nobody has been in show their error as well */
const isRevealed = ref(false);

function allowsEmpty(): boolean {
	return props.allowEmpty || (parent?.allowsEmpty() ?? false);
}

function hidesRequiredMarks(): boolean {
	return props.hideRequiredMarks || (parent?.hidesRequiredMarks() ?? false);
}

function revealed(): boolean {
	return isRevealed.value || (parent?.revealed() ?? false);
}

/**
 * Shows every error, the untouched empty fields too, and moves to the first one. For the
 * submit of a form that would otherwise be refused with nothing on the screen saying why.
 */
async function reveal(): Promise<void> {
	isRevealed.value = true;
	await nextTick();

	const invalid = rootRef.value?.querySelector<HTMLElement>('.is-invalid');
	if (!invalid) return;
	const target = invalid.matches(FOCUSABLE) ? invalid : invalid.querySelector<HTMLElement>(FOCUSABLE);
	const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
	invalid.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
	target?.focus({ preventScroll: true });
}

/*
 * Registry - maps each field's uid to its isValid computed.
 * Vue tracks reactive Map operations (set/delete/size/values),
 * so isValid below reacts whenever fields mount, unmount, or change state.
 */
const registry = reactive(new Map<string, ComputedRef<boolean>>());

function register(id: string, isValid: ComputedRef<boolean>): void {
	registry.set(id, isValid);
}

function unregister(id: string): void {
	registry.delete(id);
}

/*
 * True when every registered field passes its own validations.
 * Array.every() on an empty iterable returns true (vacuous truth),
 * so a form with no registered fields is considered valid.
 */
const isValid = computed<boolean>(() => [...registry.values()].every((v) => v.value));

/*
 * A form inside a form is one more field to the one around it, so a dialog that wraps its own
 * fields keeps working when the dialog itself provides a form: nesting composes rather than
 * hiding the inner fields from the outer verdict.
 */
onMounted(() => parent?.register(uid, isValid));
onUnmounted(() => parent?.unregister(uid));

provide(efFormSymbol, { register, unregister, allowsEmpty, revealed, hidesRequiredMarks });

defineExpose({ isValid, reveal });
</script>
